import { useState, useEffect, useRef, useCallback } from 'react';
import { VOICEFLOW_CONFIG } from '../components/chat/VoiceflowConfig';

/** Escapa un texto para poder usarlo dentro de content: "..." en CSS */
const cssEscape = (text) => String(text || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');

/**
 * Todos los shadow roots colgando de un nodo, en profundidad.
 *
 * El widget de Voiceflow se monta con attachShadow({mode:'open'}) sobre el
 * propio contenedor en modo embedded, pero puede anidar más de un shadow root.
 * querySelectorAll('*') NO atraviesa shadow roots, así que hay que recorrerlos
 * a mano o los estilos y el título no alcanzan al widget.
 */
const collectShadowRoots = (root, found = []) => {
  if (!root) return found;
  const nodes = root.shadowRoot ? [root] : [];
  if (root.querySelectorAll) nodes.push(...Array.from(root.querySelectorAll('*')));
  nodes.forEach((el) => {
    if (el.shadowRoot && !found.includes(el.shadowRoot)) {
      found.push(el.shadowRoot);
      collectShadowRoots(el.shadowRoot, found);
    }
  });
  return found;
};

export const useChatWidget = () => {
  const [widgetVisible, setWidgetVisible] = useState(false);
  const [widgetPosition, setWidgetPosition] = useState({ x: null, y: null });
  const [widgetSize, setWidgetSize] = useState(() => ({ width: 450, height: Math.round(window.innerHeight * 0.55) }));
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [widgetMinimized, setWidgetMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [boolVoiceflowNavigation, setboolVoiceflowNavigation] = useState(false);
  const [boolAutoNavigation, setboolAutoNavigation] = useState(false);

  const widgetContainerRef = useRef(null);
  // Div interno donde Voiceflow monta el widget.
  //
  // Es un nodo aparte del contenedor de React a propósito: attachShadow sólo
  // puede llamarse UNA vez por elemento, así que para recargar el widget hay
  // que darle un nodo virgen. Si Voiceflow se montara sobre el contenedor que
  // React controla, no podríamos reemplazarlo sin romper el árbol de React.
  const vfTargetRef = useRef(null);
  const voiceflowRef = useRef(null);
  // Título mostrado en la cabecera del widget. Voiceflow sólo lo lee al montar,
  // así que para cambiarlo por sala hay que pisarlo desde fuera. Se guarda en
  // estado (regenera el CSS) y en un ref (lectura inmediata desde el intervalo).
  const [widgetTitle, setWidgetTitle] = useState(VOICEFLOW_CONFIG.widget.title);
  const widgetTitleRef = useRef(VOICEFLOW_CONFIG.widget.title);

  const calculateCenteredRightPosition = useCallback(() => {
    const rightMargin = 400;
    const x = window.innerWidth - widgetSize.width - rightMargin;
    const y = (window.innerHeight - widgetSize.height) / 2.3;
    return { x: Math.max(0, x), y: Math.max(0, y) };
  }, [widgetSize]);

  const calculateCenteredLeftPosition = useCallback(() => {
    const leftMargin = 400;
    const x = leftMargin;
    const y = (window.innerHeight - widgetSize.height) / 2;
    return { x: Math.max(0, x), y: Math.max(0, y) };
  }, [widgetSize]);


  const calculateCenteredBottomPosition = () => {
    const container = widgetContainerRef.current;
    if (!container) return { x: 0, y: 0 };

    const width = widgetSize.width;
    const height = widgetSize.height; // Ajuste de altura para incluir margen inferior
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const padding = 16; // px desde bordes
    const bottomOffset = 5; // Añadido de altura del chat desde el borde inferior
    const x = (viewportWidth - width) / 2;
    const y = viewportHeight - height - padding - bottomOffset;

    return { x: Math.max(padding, x), y: Math.max(padding, y) };
  };

  // Crea el div interno donde monta Voiceflow. Se hace de forma imperativa (no
  // en el JSX) para que React no lo reconcilie: reloadWidgetSilent lo sustituye
  // por un nodo nuevo y React reinsertaría el viejo al re-renderizar.
  useEffect(() => {
    const container = widgetContainerRef.current;
    if (!container || vfTargetRef.current) return;
    const el = document.createElement('div');
    el.setAttribute('data-vf-target', '');
    el.style.cssText = 'width:100%;height:100%;';
    container.appendChild(el);
    vfTargetRef.current = el;
  }, []);

  const initializeWidget = useCallback(() => {
    if (widgetLoaded || !window) return;

    const existingScript = document.getElementById('voiceflow-widget-script');

    if (existingScript) {
      if (window.voiceflow && window.voiceflow.chat) {
        loadWidget();
      }
      return;
    }

    const script = document.createElement('script');
    script.id = 'voiceflow-widget-script';
    script.src = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      //console.log('Script de Voiceflow cargado');
      loadWidget();
    };

    script.onerror = () => {
      console.error('Error al cargar el script de Voiceflow');
      setWidgetLoaded(false);
      setTimeout(() => {
        //console.log('Reintentando carga del script de Voiceflow...');
        document.body.appendChild(script);
      }, 2000);
    };

    document.body.appendChild(script);
  }, [widgetLoaded]);

  const loadWidget = useCallback(() => {
    if (!window.voiceflow || !window.voiceflow.chat) {
      console.error('Voiceflow no está disponible');
      return;
    }

    try {
      const trace_debugger = {
        name: 'trace_debugger',
        type: 'effect',
        match: ({ trace }) => {
          try { console.log('[VF-TRACE]', trace.type, typeof trace.payload === 'string' ? trace.payload.substring(0, 150) : trace.payload?.message?.substring(0, 150) || ''); } catch(e) {}
          return false;
        },
        effect: () => {}
      };

      // 'effect' y no 'response': una extensión de tipo response empuja SIEMPRE
      // un mensaje a la conversación aunque render devuelva null, y esa burbuja
      // vacía se veía como un punto/línea en blanco antes del texto del agente
      const navigate_to_page = {
        name: 'navigate_to_page',
        type: 'effect',
        match: ({ trace }) => trace.type === 'navigate_to_page',
        effect: ({ trace }) => {
          try {
            let parsed;
            if (typeof trace.payload === 'string') {
              parsed = JSON.parse(trace.payload);
            } else if (typeof trace.payload === 'object' && trace.payload !== null) {
              parsed = trace.payload;
            } else {
              return;
            }
            let page = parsed.page;
            if (page == null && parsed.body) {
              const body = typeof parsed.body === 'string' ? JSON.parse(parsed.body) : parsed.body;
              page = body.page;
            }
            if (page == null) {
              console.warn('[VF-NAV] campo page no encontrado:', parsed);
              return;
            }
            console.log('[VF-NAV] navigate_to_page -> pagina:', page);
            // source: quien escucha distingue esta navegación (ordenada por el
            // propio agente) de las que origina el usuario, que sí deben
            // avisar a Voiceflow con un intent pageNavigationInfo
            window.dispatchEvent(new CustomEvent('voiceflow-navigate', {
              detail: { page: parseInt(page), source: 'voiceflow' }
            }));
          } catch (error) {
            console.error('[VF-NAV] Error:', error);
          }
        }
      };

      const open_url = {
        name: 'open_url',
        type: 'effect',
        match: ({ trace }) => trace.type === 'open_url',
        effect: ({ trace }) => {
          try {
            const payload = typeof trace.payload === 'string'
              ? JSON.parse(trace.payload)
              : trace.payload;
            const url = payload?.url;
            if (url) window.location.href = url;
          } catch (error) {
            console.error('Error en open_url:', error);
          }
        }
      };

      const widgetConfig = {
        verify: {
          projectID: VOICEFLOW_CONFIG.projectID,
          apiKey: VOICEFLOW_CONFIG.apiKey
        },
        url: 'https://general-runtime.voiceflow.com',
        versionID: 'production',
        render: {
          mode: 'embedded',
          target: vfTargetRef.current || widgetContainerRef.current
        },
        autostart: true,
        allowDownloads: false,
        assistant: {
          persistence: 'memory',
          extensions: [trace_debugger, navigate_to_page, open_url],
          // Título de la sala actual, no el genérico: así una recarga del
          // widget (reloadWidgetSilent) no revierte la cabecera
          title: widgetTitleRef.current,
          subtitle: VOICEFLOW_CONFIG.widget.subtitle,
          primaryColor: VOICEFLOW_CONFIG.widget.primaryColor,
          backgroundColor: VOICEFLOW_CONFIG.widget.backgroundColor,
          avatar: {
            src: VOICEFLOW_CONFIG.widget.avatar.src,
            alt: VOICEFLOW_CONFIG.widget.avatar.alt
          },
          chat: {
            welcomeMessage: VOICEFLOW_CONFIG.widget.chat.welcomeMessage,
            inputPlaceholder: VOICEFLOW_CONFIG.widget.chat.inputPlaceholder,
            audio: {
              enabled: VOICEFLOW_CONFIG.widget.chat.audio.enabled === 'true' || VOICEFLOW_CONFIG.widget.chat.audio.enabled === true,
              autoplay: VOICEFLOW_CONFIG.widget.chat.audio.autoplay === 'true' || VOICEFLOW_CONFIG.widget.chat.audio.autoplay === true
            }
          }
        }
      };

      window.voiceflow.chat.load(widgetConfig);
      voiceflowRef.current = window.voiceflow;
      setWidgetLoaded(true);
    } catch (error) {
      console.error('Error al inicializar el widget de Voiceflow:', error);
      setWidgetLoaded(false);
    }
  }, [widgetContainerRef]);

  // Recrea el widget con sesión limpia.
  // launchEvent (opcional): request con el que se ABRE la conversación, en vez
  // del launch por defecto del widget. Es la única forma de evitar el saludo de
  // recepción: el widget siempre arranca la conversación al montarse y, sin
  // este override, manda { type: 'launch' } → Start → bloque "Bienvenida".
  const reloadWidgetSilent = useCallback((launchEvent) => {
    if (!window.voiceflow || !window.voiceflow.chat) return;
    try {
      try { window.voiceflow.chat.destroy(); } catch (e) {}

      // destroy() NO desprende el shadow root del nodo, y attachShadow sólo
      // admite una llamada por elemento: reutilizarlo lanza
      //   "Shadow root cannot be created on a host which already hosts a shadow tree"
      // y Voiceflow lo convierte en "Failed to attach embedded chat".
      // Por eso se sustituye el div interno por uno recién creado.
      const stale = vfTargetRef.current;
      if (stale && stale.parentNode) {
        const fresh = document.createElement('div');
        fresh.className = stale.className;
        fresh.style.cssText = stale.style.cssText;
        stale.parentNode.replaceChild(fresh, stale);
        vfTargetRef.current = fresh;
      } else if (stale) {
        stale.innerHTML = '';
      }

      const trace_debugger = {
        name: 'trace_debugger',
        type: 'effect',
        match: ({ trace }) => {
          try { console.log('[VF-TRACE]', trace.type, typeof trace.payload === 'string' ? trace.payload.substring(0, 150) : trace.payload?.message?.substring(0, 150) || ''); } catch(e) {}
          return false;
        },
        effect: () => {}
      };

      // Ver nota en loadWidget: 'effect' evita la burbuja vacía en el chat
      const navigate_to_page = {
        name: 'navigate_to_page',
        type: 'effect',
        match: ({ trace }) => trace.type === 'navigate_to_page',
        effect: ({ trace }) => {
          try {
            let parsed;
            if (typeof trace.payload === 'string') {
              parsed = JSON.parse(trace.payload);
            } else if (typeof trace.payload === 'object' && trace.payload !== null) {
              parsed = trace.payload;
            } else { return; }
            let page = parsed.page;
            if (page == null && parsed.body) {
              const body = typeof parsed.body === 'string' ? JSON.parse(parsed.body) : parsed.body;
              page = body.page;
            }
            if (page == null) { return; }
            console.log('[VF-NAV] navigate_to_page -> pagina:', page);
            window.dispatchEvent(new CustomEvent('voiceflow-navigate', { detail: { page: parseInt(page), source: 'voiceflow' } }));
          } catch (error) { console.error('[VF-NAV] Error:', error); }
        }
      };

      const open_url = {
        name: 'open_url',
        type: 'effect',
        match: ({ trace }) => trace.type === 'open_url',
        effect: ({ trace }) => {
          try {
            const payload = typeof trace.payload === 'string' ? JSON.parse(trace.payload) : trace.payload;
            const url = payload?.url;
            if (url) window.location.href = url;
          } catch (error) { console.error('Error en open_url:', error); }
        }
      };

      const loaded = window.voiceflow.chat.load({
        verify: { projectID: VOICEFLOW_CONFIG.projectID, apiKey: VOICEFLOW_CONFIG.apiKey },
        url: 'https://general-runtime.voiceflow.com',
        versionID: 'production',
        render: { mode: 'embedded', target: vfTargetRef.current || widgetContainerRef.current },
        autostart: !!launchEvent,
        ...(launchEvent ? { launch: { event: launchEvent } } : {}),
        allowDownloads: false,
        assistant: {
          persistence: 'memory',
          extensions: [trace_debugger, navigate_to_page, open_url],
          // Título de la sala actual, no el genérico: así una recarga del
          // widget (reloadWidgetSilent) no revierte la cabecera
          title: widgetTitleRef.current,
          subtitle: VOICEFLOW_CONFIG.widget.subtitle,
          primaryColor: VOICEFLOW_CONFIG.widget.primaryColor,
          backgroundColor: VOICEFLOW_CONFIG.widget.backgroundColor,
          avatar: { src: VOICEFLOW_CONFIG.widget.avatar.src, alt: VOICEFLOW_CONFIG.widget.avatar.alt },
          chat: {
            welcomeMessage: '',
            inputPlaceholder: VOICEFLOW_CONFIG.widget.chat.inputPlaceholder,
            audio: {
              enabled: VOICEFLOW_CONFIG.widget.chat.audio.enabled === 'true' || VOICEFLOW_CONFIG.widget.chat.audio.enabled === true,
              autoplay: VOICEFLOW_CONFIG.widget.chat.audio.autoplay === 'true' || VOICEFLOW_CONFIG.widget.chat.audio.autoplay === true
            }
          }
        }
      });
      voiceflowRef.current = window.voiceflow;
      setWidgetLoaded(true);
      console.log('[VF] Widget recreado. Request de apertura:', launchEvent?.payload?.query || 'ninguno');
      // Devuelve la promesa de load() (si el bundle la expone) para poder
      // encadenar el primer interact cuando el widget ya está montado
      return loaded;
    } catch (error) {
      console.error('[VF] Error al recargar widget:', error);
    }
  }, [widgetContainerRef]);

  const generateCustomStyles = useCallback(() => {
    let styles = `
      .vfrc-widget {
        z-index: 9999 !important;
      }
      .vfrc-launcher {
        display: none !important;
      }
      .vfrc-chat {
        position: relative !important;
        right: auto !important;
        bottom: auto !important;
        left: 0 !important;
        top: 0 !important;
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        border-radius: 12px !important;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
        background-color: #f9fafb;
      }
      /* Reducción de cabecera / borde superior azul para maximizar espacio de texto */
      .vfrc-header {
        padding: 6px 12px !important;
        min-height: 38px !important;
        max-height: 44px !important;
        display: flex !important;
        align-items: center !important;
      }
      .vfrc-header--title {
        font-size: 13px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        margin: 0 !important;
      }
      .vfrc-header--subtitle,
      .vfrc-header--description {
        display: none !important;
      }
      .vfrc-avatar,
      .vfrc-header .vfrc-avatar {
        width: 24px !important;
        height: 24px !important;
        min-width: 24px !important;
        min-height: 24px !important;
      }
      .vfrc-header--actions button {
        padding: 4px !important;
        width: 26px !important;
        height: 26px !important;
      }
      .vfrc-chat-dialog,
      .vfrc-chat--dialog,
      .vfrc-dialog {
        padding: 8px 10px !important;
      }
      .vfrc-message {
        font-size: 13px !important;
        line-height: 1.35 !important;
      }

      /* Título de la sala.
         Voiceflow sólo lee assistant.title al montar el widget y React
         reescribe el nodo en cada render, así que pisar textContent no aguanta.
         Con ::after el texto lo pone el CSS: React no lo toca nunca. Se oculta
         el texto original con font-size:0 en el contenedor. */
      .vfrc-header--title,
      [class*="header--title"] {
        font-size: 0 !important;
      }
      .vfrc-header--title::after,
      [class*="header--title"]::after {
        content: "${cssEscape(widgetTitle)}";
        font-size: 13px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
        white-space: nowrap;
      }
    `;
    return styles;
  }, [widgetTitle]);

  useEffect(() => {
    if (!widgetLoaded || !window.voiceflow) return;

    const applyStyles = () => {
      const css = generateCustomStyles();

      // Inyección en el documento principal
      let styleElement = document.getElementById('voiceflow-custom-styles');
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = 'voiceflow-custom-styles';
        document.head.appendChild(styleElement);
      }
      if (styleElement.textContent !== css) styleElement.textContent = css;

      // Se recorre desde document: cubre tanto el contenedor embebido como el
      // div #voiceflow-chat que el bundle cuelga de <body> por su cuenta
      collectShadowRoots(document.documentElement).forEach((shadow) => {
        let shadowStyle = shadow.getElementById && shadow.getElementById('voiceflow-shadow-styles');
        if (!shadowStyle) {
          shadowStyle = document.createElement('style');
          shadowStyle.id = 'voiceflow-shadow-styles';
          shadow.appendChild(shadowStyle);
        }
        if (shadowStyle.textContent !== css) shadowStyle.textContent = css;
      });
    };

    applyStyles();
    const interval = setInterval(applyStyles, 500);
    return () => clearInterval(interval);
  }, [widgetLoaded, generateCustomStyles]);

  // Cambia el título de la cabecera del widget (una sala por página).
  // El repintado real lo hace el CSS de generateCustomStyles; aquí sólo se
  // actualiza el estado, que es lo que regenera esa hoja de estilos.
  const updateWidgetTitle = useCallback((newTitle) => {
    if (!newTitle) return;
    if (widgetTitleRef.current === newTitle) return;
    widgetTitleRef.current = newTitle;
    setWidgetTitle(newTitle);
  }, []);

  const sendMessage = useCallback((message) => {
    if (!voiceflowRef.current || !widgetLoaded) {
      console.warn('Widget no está listo para recibir mensajes');
      return;
    }
    try {
      voiceflowRef.current.chat.interact({
        type: 'text',
        payload: message
      });
      console.log('Mensaje enviado:', message);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
    }
  }, [widgetLoaded]);

  const sendIntent = useCallback((contextIntent) => {
    if (!voiceflowRef.current || !widgetLoaded) {
      console.warn('Widget no listo para recibir intents');
      return;
    }
    try {
      console.log('[VF-Intent] Enviando intent:', contextIntent?.payload?.query || contextIntent?.type);
      // Se devuelve la promesa: si la sesión aún no está lista el widget la
      // rechaza ("Socket.io session not ready") y quien llama puede reintentar
      return voiceflowRef.current.chat.interact(contextIntent);
    } catch (error) {
      console.error('Error al enviar intent:', error);
    }
  }, [widgetLoaded]);

  const sendCustomAction = useCallback((action, data = {}) => {
    if (!voiceflowRef.current || !widgetLoaded) {
      console.warn('Widget no está listo para recibir acciones');
      return;
    }
    try {
      voiceflowRef.current.chat.interact({
        type: 'custom',
        payload: {
          action: action,
          data: data
        }
      });
      //console.log('Acción personalizada enviada:', action, data);
    } catch (error) {
      console.error('Error al enviar acción personalizada:', error);
    }
  }, [widgetLoaded]);

  const sendCurrentPage = useCallback((pageNumber, pageTitle) => {
    if (!voiceflowRef.current || !widgetLoaded) {
      //console.warn('Widget no está listo para recibir información de página');
      return;
    }
    try {
      sendCustomAction('page_navigation', {
        pageNumber: pageNumber,
        pageTitle: pageTitle,
        timestamp: new Date().toISOString()
      });
      //console.log(`Página actual enviada a Voiceflow: ${pageNumber} - ${pageTitle}`);
    } catch (error) {
      console.error('Error al enviar página actual:', error);
    }
  }, [widgetLoaded, sendCustomAction]);

  const openWidget = useCallback(() => {
    if (!voiceflowRef.current || !widgetLoaded) {
      console.warn('Widget no está listo para abrirse');
      return;
    }
    try {
      voiceflowRef.current.chat.open();
      setWidgetVisible(true);
      setWidgetMinimized(false);
      //console.log('Widget abierto');
    } catch (error) {
      console.error('Error al abrir el chat:', error);
    }
  }, [widgetLoaded]);

  const closeWidget = useCallback(() => {
    if (!voiceflowRef.current || !widgetLoaded) {
      return;
    }
    try {
      voiceflowRef.current.chat.close();
      setWidgetVisible(false);
      //console.log('Widget cerrado');
    } catch (error) {
      console.error('Error al cerrar el chat:', error);
    }
  }, [widgetLoaded]);

  const toggleWidget = useCallback(() => {
    if (widgetVisible) {
      closeWidget();
    } else {
      openWidget();
    }
  }, [widgetVisible, openWidget, closeWidget]);

  const toggleMinimize = useCallback(() => {
    if (!voiceflowRef.current || !widgetLoaded) return;
    try {
      if (widgetMinimized) {
        voiceflowRef.current.chat.maximize();
      } else {
        voiceflowRef.current.chat.minimize();
      }
      setWidgetMinimized(!widgetMinimized);
    } catch (error) {
      console.error('Error al minimizar/maximizar:', error);
    }
  }, [widgetLoaded, widgetMinimized]);

  const updatePosition = useCallback((x, y) => {
    setWidgetPosition({ x, y });
  }, []);

  const updateSize = useCallback((width, height) => {
    setWidgetSize({ width, height });
  }, []);

  const resetPosition = useCallback(() => {
    setWidgetPosition({ x: null, y: null });
  }, []);

  const resetSize = useCallback(() => {
    setWidgetSize({ width: 450, height: Math.round(window.innerHeight * 0.55) });
  }, []);

  const centerWidgetR = useCallback(() => {
    const centeredPosition = calculateCenteredRightPosition();
    setWidgetPosition(centeredPosition);
  }, [calculateCenteredRightPosition]);

  const centerWidgetL = useCallback(() => {
    const centeredPosition = calculateCenteredLeftPosition();
    setWidgetPosition(centeredPosition);
  }, [calculateCenteredLeftPosition]);

  const destroyWidget = useCallback(() => {
    if (voiceflowRef.current && voiceflowRef.current.chat) {
      try {
        voiceflowRef.current.chat.destroy();
      } catch (error) {
        console.error('Error al destruir widget:', error);
      }
    }
    setWidgetLoaded(false);
    setWidgetVisible(false);
    voiceflowRef.current = null;
  }, []);

  const handleDragStart = useCallback((e) => {
    if (e.target.classList.contains('resize-handle')) return;
    setIsDragging(true);
    const rect = widgetContainerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleResizeStart = useCallback((e) => {
    e.stopPropagation();
    setIsResizing(true);
    setDragOffset({
      x: e.clientX,
      y: e.clientY
    });
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setWidgetPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!isResizing) return;
      const dx = e.clientX - dragOffset.x;
      const dy = e.clientY - dragOffset.y;
      setWidgetSize(prev => ({
        width: Math.max(200, prev.width + dx),
        height: Math.max(200, prev.height + dy)
      }));
      setDragOffset({ x: e.clientX, y: e.clientY });
    };

    const handleResizeUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeUp);
    };
  }, [isResizing, dragOffset]);

  useEffect(() => {
    const handleMessage = (event) => {
      const expectedOrigins = [
        window.location.origin,
        'https://general-runtime.voiceflow.com'
      ];
      if (!expectedOrigins.includes(event.origin)) {
        console.warn('Mensaje recibido desde un origen no permitido:', event.origin);
        return;
      }
      if (event.data.type === 'VOICEFLOW_READY') {
        setWidgetLoaded(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return {
    widgetVisible,
    widgetPosition,
    widgetSize,
    widgetLoaded,
    widgetMinimized,
    isDragging,
    isResizing,
    dragOffset,
    widgetContainerRef,
    vfTargetRef,
    initializeWidget,
    reloadWidgetSilent,
    openWidget,
    closeWidget,
    toggleWidget,
    toggleMinimize,
    sendMessage,
    sendIntent,
    sendCustomAction,
    sendCurrentPage,
    updatePosition,
    updateSize,
    resetPosition,
    resetSize,
    calculateCenteredRightPosition,
    calculateCenteredLeftPosition,
    calculateCenteredBottomPosition,
    centerWidgetR,
    centerWidgetL,
    destroyWidget,
    handleDragStart,
    handleResizeStart,
    setWidgetVisible,
    setWidgetPosition,
    setWidgetSize,
    setIsDragging,
    setIsResizing,
    setDragOffset,
    setboolAutoNavigation,
    boolAutoNavigation,
    updateWidgetTitle
  };
};